import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { CtaBusyAction, CtaConfiguration, CtaError } from '@typesApp/Cta'
import { configApi } from '@redux/queries/config'
import { connectorSlice } from './connectorSlice'

export type CtaState = {
  ctaError: CtaError
  busyAction: CtaBusyAction
  prevBusyAction: CtaBusyAction
  configuration: CtaConfiguration
}

const initialState = {
  ctaError: CtaError.NONE,
  busyAction: CtaBusyAction.NONE,
  prevBusyAction: CtaBusyAction.NONE,
} as CtaState

const REDUCER_PATH = 'cta'

export const ctaSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setCtaError(state, action: PayloadAction<CtaError>) {
      state.ctaError = action.payload
      state.busyAction = CtaBusyAction.NONE
      state.prevBusyAction = CtaBusyAction.NONE
    },
    clearCtaError(state) {
      state.ctaError = CtaError.NONE
    },
    setCtaBusyAction(state, action: PayloadAction<CtaBusyAction>) {
      state.prevBusyAction = state.busyAction
      state.busyAction = action.payload
    },
    clearCtaBusyAction(state) {
      state.busyAction = CtaBusyAction.NONE
      state.prevBusyAction = CtaBusyAction.NONE
    },
    resetCta(state) {
      state.busyAction = CtaBusyAction.NONE
      state.prevBusyAction = CtaBusyAction.NONE
    },
    resetCtaInitialState(state) {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Auth token fetch error
     */
    builder.addCase(connectorSlice.actions.authTokenError, (state, action) => {
      state.ctaError = CtaError.INIT_ERROR
    })

    /**
     * Update cta configuration with initial / logged out state
     */
    builder.addMatcher(configApi.endpoints.getCtaInitialConfiguration.matchFulfilled, (state, action) => {
      state.configuration = action.payload
    })

    /**
     * Update cta configuration retaining loggedOut configuration
     */
    builder.addMatcher(configApi.endpoints.getCtaConfiguration.matchFulfilled, (state, action) => {
      state.configuration = {
        ...action.payload,
        loggedOut: state.configuration?.loggedOut,
      }
    })
  },
})

export const { resetCta, setCtaError, clearCtaError, setCtaBusyAction, clearCtaBusyAction, resetCtaInitialState } =
  ctaSlice.actions

export const ctaErrorSelector = (state: AppState): CtaError => state.cta.ctaError
export const ctaBusyActionSelector = (state: AppState): CtaBusyAction => state.cta.busyAction
export const ctaInitErrorSelector = (state: AppState): boolean => state.cta.ctaError === CtaError.INIT_ERROR
export const ctaPreventInputSelector = (state: AppState): boolean => state.cta.busyAction !== CtaBusyAction.NONE
export const ctaConfigurationSelector = (state: AppState): CtaConfiguration => state.cta.configuration
